import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api, getCurrentUser } from "@/lib/apiClient";
import { toast } from "sonner";
import { PN_TYPE_LABELS } from "@/types/design";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  designProjectId: string;
  partNumbers: any[];
  onSaved?: () => void;
}

interface MatRow { source: string; material_id: string; qty: string; }
const emptyMat = (): MatRow => ({ source: "raw_materials", material_id: "", qty: "" });

export function MachiningOrderDialog({ open, onOpenChange, designProjectId, partNumbers, onSaved }: Props) {
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [f, setF] = useState({ design_part_number_id: "", machine_id: "", notes: "" });
  const [mats, setMats] = useState<MatRow[]>([emptyMat()]);

  const { data: machines } = useQuery({
    queryKey: ["cnc-machines"],
    queryFn: async () => {
      const { data } = await api.from("machines").select("id,name,type,machine_type").eq("is_active", true).order("name");
      return (data || []).filter((m: any) => /cnc|fresad|milling/i.test(`${m.type || ""} ${m.machine_type || ""}`));
    },
  });
  const { data: rawMats } = useQuery({
    queryKey: ["raw-mats-machining"],
    queryFn: async () => (await api.from("raw_materials").select("id,name,unit,quantity").eq("is_active", true).order("name")).data || [],
  });
  const { data: invMats } = useQuery({
    queryKey: ["inv-mats-machining"],
    queryFn: async () => (await api.from("material_inventory").select("id,name,unit,current_stock").eq("is_active", true).order("name")).data || [],
  });

  // Código automático OT-MEC-#####
  useEffect(() => {
    if (!open) return;
    setF({ design_part_number_id: "", machine_id: "", notes: "" });
    setMats([emptyMat()]);
    (async () => {
      const { data } = await api.from("machining_orders").select("code");
      let max = 0;
      (data || []).forEach((r: any) => { const m = (r.code || "").match(/^OT-MEC-(\d+)$/); if (m) max = Math.max(max, parseInt(m[1], 10)); });
      setCode(`OT-MEC-${String(max + 1).padStart(5, "0")}`);
    })();
  }, [open]);

  const matsFor = (source: string) => source === "raw_materials" ? (rawMats || []) : source === "material_inventory" ? (invMats || []) : [];
  const setMat = (i: number, patch: Partial<MatRow>) => setMats((p) => p.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const valid = mats.filter((m) => m.source !== "none" && m.material_id && m.qty);
      const first = valid[0];
      const firstRes: any = first ? matsFor(first.source).find((x: any) => x.id === first.material_id) : null;
      const { data: ins, error } = await api.from("machining_orders").insert([{
        code,
        design_project_id: designProjectId,
        design_part_number_id: f.design_part_number_id || null,
        machine_id: f.machine_id || null,
        material_source: first ? first.source : "none",
        material_id: first?.material_id || null,
        material_name: valid.length > 1 ? `${firstRes?.name || "Material"} +${valid.length - 1} más` : (firstRes?.name || null),
        material_qty: valid.length === 1 && first ? parseFloat(first.qty) : null,
        material_unit: firstRes?.unit || null,
        notes: f.notes || null,
        status: "pending",
        created_by: getCurrentUser()?.id || null,
      }]).select("id");
      if (error) throw error;
      const orderId = ins?.[0]?.id;
      if (orderId && valid.length) {
        const rows = valid.map((m) => {
          const r: any = matsFor(m.source).find((x: any) => x.id === m.material_id);
          return { machining_order_id: orderId, material_source: m.source, material_id: m.material_id, material_name: r?.name || null, material_qty: parseFloat(m.qty), material_unit: r?.unit || null };
        });
        const { error: me } = await api.from("machining_order_materials").insert(rows);
        if (me) throw me;
      }
      toast.success(`OT de mecanizado creada: ${code}`);
      onOpenChange(false);
      onSaved?.();
    } catch (err: any) {
      toast.error(err.message || "No se pudo crear la OT");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nueva OT de Mecanizado</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Código OT (automático)</Label>
              <Input value={code} readOnly className="font-mono bg-muted/40" />
            </div>
            <div>
              <Label>Fresadora (CNC)</Label>
              <Select value={f.machine_id || "none"} onValueChange={(v) => setF({ ...f, machine_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {machines?.map((m: any) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>PN a mecanizar</Label>
            <Select value={f.design_part_number_id || "none"} onValueChange={(v) => setF({ ...f, design_part_number_id: v === "none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="Seleccionar PN (molde/utillaje/pieza)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin PN</SelectItem>
                {partNumbers?.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.pn_code} · {PN_TYPE_LABELS[p.pn_type]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Materiales (multi) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Materias primas / materiales</Label>
              <Button type="button" size="sm" variant="outline" className="gap-1" onClick={() => setMats([...mats, emptyMat()])}><Plus className="w-4 h-4" />Material</Button>
            </div>
            {mats.map((m, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4">
                  {i === 0 && <Label className="text-[11px]">Origen</Label>}
                  <Select value={m.source} onValueChange={(v) => setMat(i, { source: v, material_id: "" })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="raw_materials">Materias primas</SelectItem>
                      <SelectItem value="material_inventory">Preimpregnados</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-5">
                  {i === 0 && <Label className="text-[11px]">Material</Label>}
                  <Select value={m.material_id || "none"} onValueChange={(v) => setMat(i, { material_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {matsFor(m.source).map((x: any) => <SelectItem key={x.id} value={x.id}>{x.name} ({x.unit || ""})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  {i === 0 && <Label className="text-[11px]">Cantidad</Label>}
                  <Input type="number" step="0.001" value={m.qty} onChange={(e) => setMat(i, { qty: e.target.value })} placeholder="0" className="h-9" />
                </div>
                <div className="col-span-1">
                  <Button type="button" size="icon" variant="ghost" className="h-9 w-9 text-destructive" onClick={() => setMats(mats.length > 1 ? mats.filter((_, idx) => idx !== i) : [emptyMat()])}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">Ej.: 1 placa de 100 + ½ de 50. El stock se descuenta al gestionar la OT.</p>
          </div>

          <div>
            <Label>Notas</Label>
            <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={loading}>{loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Crear OT</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
